import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import axiosClient from '@/api/axiosClient';
import { getUploadFilesApi } from '@/api/endpoints/upload';
import { downloadBlob } from '@/utils/download';
import { useNotificationsStore, type Problem } from '@/store/notificationsStore';
import { useGlobalStore } from '@/store/storeGlobal';

const debugPrefix = '[Upload]';
const uploadApiBasePath = 'v2/upload';
const uploadStatusFallbackDelayMs = 2500;

// WI 2303: blokk-størrelse + parallellitet for direkte block-blob-PUT. 8 MiB-blokker
// holder antall blokker innenfor Azures grense (50 000) for multi-GB-filer, og 5
// samtidige blokker gir god gjennomstrømning uten å overbelaste nettleseren.
const directUploadBlockSizeBytes = 8 * 1024 * 1024;
const directUploadConcurrency = 5;

type ContentTypeMap = Record<string, string>;
type IconTypeMap = Record<string, string[]>;

interface UploadFileItem {
  uploadId: string;
  extractedFromUploadId?: string | null;
  fileName?: string;
  contentType?: string;
  processType?: string;
  processStatus?: string;
  isComplete?: boolean;
  hasError?: boolean;
  progressPercent?: number;
  message?: string;
  currentMessage?: string;
  currentTaskName?: string;
  timestamp?: string;
  uploadedDate?: string;
  processedDate?: string;
  lastImport?: string;
  uploadedBy?: string;
  fileSize?: number;
  isInBlobStorage?: boolean;
  isExcel?: boolean;
  isTextFile?: boolean;
  canImportFile?: boolean;
  counterData?: unknown;
  isProcessing?: boolean;
  isExcelSheet?: boolean;
  isArchived?: boolean;
  terminalState?: UploadTerminalState;
  [key: string]: unknown;
}

interface EventStreamUploadUpdate {
  fileId?: string;
  processType?: string;
  processStatus?: string;
  isComplete?: boolean;
  hasError?: boolean;
  progressPercent?: number;
  message?: string;
  taskName?: string;
  timestamp?: string;
  counterData?: unknown;
}

type UploadTerminalState = 'uploading' | 'processing' | 'completed' | 'failed' | 'timedout' | 'poisoned';

interface GetFilesResponse {
  items?: UploadFileItem[];
  totalCount?: number;
}

// WI 2303: svar-kontrakter for direkte-til-blob-stien (BE-recordene i
// ICS.Contracts.Import; JSON-serialisert til camelCase).
interface IssueUploadSasResponse {
  uploadId: string;
  blobName: string;
  uploadUri: string;
  expiresAt: string;
}

interface RegisterUploadedBlobResponse {
  uploadId: string;
  processStatus: string;
}

interface GetFilesOptions {
  resetArchive?: boolean;
  append?: boolean;
}

type UploadListContext = 'archive' | 'picker';
interface InitializeFileListOptions {
  prefetchOnMount?: boolean;
  initialQuery?: string;
}

interface UploadPreviewRequest {
  requestId: number;
  rootUploadId?: string;
  previewUploadId?: string;
}
type UploadPreviewRequestHandler = (payload: UploadPreviewRequest) => void | Promise<void>;

type UploadSelectedFileChangeHandler = (uploadId: string | null) => void;

interface WaitForUploadOutcomeOptions {
  timeoutMs?: number;
  pollIntervalMs?: number;
  notifyOnFailure?: boolean;
  onProgressMessage?: (message: string) => void;
}

interface WaitForUploadOutcomeResult {
  file: UploadFileItem | null;
  isReadyForPreview: boolean;
  isTerminalFailure: boolean;
  didTimeout: boolean;
  resultMessage: string | null;
}

export function humanReadableFileSize (bytes: number): string {
  const thresh = 1000;
  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let u = -1;
  let current = bytes;
  do {
    current /= thresh;
    ++u;
  } while (Math.abs(current) >= thresh && u < units.length - 1);

  return current >= 100
    ? `${Math.round(current)}${units[u]}`
    : `${current.toFixed(0).replace('.', ',')}${units[u]}`;
}

function dictionaryHasKey (dictionary: Record<string, unknown>, contentType?: string): boolean {
  return Boolean(contentType && Object.hasOwn(dictionary, contentType));
}

function getErrorDetail (error: unknown, fallbackDetail = 'Ukjent feil'): string {
  if (isAxiosError(error)) {
    const payload = error.response?.data as { detail?: unknown; title?: unknown } | undefined;
    if (typeof payload?.detail === 'string' && payload.detail.trim().length > 0) {
      return payload.detail;
    }
    if (typeof payload?.title === 'string' && payload.title.trim().length > 0) {
      return payload.title;
    }
    if (typeof error.message === 'string' && error.message.trim().length > 0) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }

  return fallbackDetail;
}

function toEpochMs (value?: string | null): number {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function normalizeStatus (status?: string): string {
  return String(status || '').trim().toLowerCase();
}

function normalizedUploadIdOrNull (raw: unknown): string | null {
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  return trimmed.length > 0 ? trimmed : null;
}

export const useUploadStore = defineStore('uploadStore', () => {
  const notificationsStore = useNotificationsStore();
  const globalStore = useGlobalStore();

  const uploadStatusFallbackTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // Mid-bright file-type-ikoner: god kontrast på både lys og mørk bakgrunn,
  // og beholder semantisk farge-assosiasjon (grønn = Excel, blå = CSV, osv.).
  // Tidligere bruk av `darkblue`/`darkgreen` ble usynlig mot dark-tema.
  const colors: ContentTypeMap = {
    'application/vnd.ms-excel': '#21a366',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '#21a366',
    'text/csv': '#5b9bd5',
    'application/json': '#a0a0a0',
    'text/plain': '#daa520',
    'text/xml': '#a0a0a0',
    'application/pdf': '#e74c3c',
    'application/zip': '#f39c12',
    'application/x-zip-compressed': '#f39c12',
    'application/gzip': '#9b59b6',
    'application/x-gzip': '#9b59b6',
    'application/gzip-compressed': '#9b59b6',
  };

  const icons: IconTypeMap = {
    'application/vnd.ms-excel': ['fal', 'file-excel'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['fal', 'file-excel'],
    'text/csv': ['fal', 'file-csv'],
    'application/json': ['fal', 'file-code'],
    'text/plain': ['fal', 'file-lines'],
    'text/xml': ['fal', 'file-code'],
    'application/pdf': ['fal', 'file-pdf'],
    'application/zip': ['fal', 'file-zipper'],
    'application/x-zip-compressed': ['fal', 'file-zipper'],
    'application/gzip': ['fal', 'file-zipper'],
    'application/x-gzip': ['fal', 'file-zipper'],
    'application/gzip-compressed': ['fal', 'file-zipper'],
  };

  const files = ref<UploadFileItem[]>([]);
  const loading = ref(false);
  const findImportDefinitionStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
  const findImportDefinitionError = ref<string | null>(null);
  const recalculateStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
  const recalculateError = ref<string | null>(null);
  const query = ref('');
  const showArchive = ref(false);
  // WI 2286: Excel-/zip-arkiver vises sammenslått (collapsed) som standard.
  // Per-rad expand-state eies av storen; reaktivt Set så `isExpanded` sporer i computeds.
  const expandedUploadIds = reactive(new Set<string>());
  const pageSize = ref(20);
  const currentPage = ref(1);
  const listContext = ref<UploadListContext>('picker');
  const uploadedInSessionIds = ref<string[]>([]);
  const pickerSearchActivated = ref(false);
  const totalFilesCount = ref(0);
  const selectedStatuses = ref<string[]>([]);
  const selectedFile = ref<UploadFileItem | null>(null);
  const previewRequest = ref<UploadPreviewRequest | null>(null);
  const previewRequestCounter = ref(0);
  const previewRequestHandlerCounter = ref(0);
  const previewRequestHandlers = new Map<number, UploadPreviewRequestHandler>();
  const selectedFileChangeHandlers = new Map<number, UploadSelectedFileChangeHandler>();
  let selectedFileChangeHandlerNextId = 0;
  let previousNotifiedSelectedUploadId: string | null | undefined = undefined;
  const uploadProgressPercent = ref(0);
  const uploadShowProgressBar = ref(false);
  const uploadOverlayVisible = ref(false);
  const uploadOverlayMessage = ref('');
  const uploadOutcomeState = ref<'idle' | 'success' | 'error'>('idle');
  const uploadOutcomeMessage = ref<string | null>(null);

  function buildProblemFromError (error: unknown, fallbackTitle: string): Problem | null {
    if (!isAxiosError(error)) {
      return null;
    }

    const payload = error.response?.data as Partial<Problem> | undefined;
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const title = typeof payload.title === 'string' && payload.title.trim().length > 0
      ? payload.title
      : fallbackTitle;
    const detail = typeof payload.detail === 'string' ? payload.detail : undefined;
    const errors = payload.errors;
    return { title, detail, errors };
  }

  function notifyApiError (summary: string, error: unknown, fallbackDetail = 'Ukjent feil'): string {
    const problem = buildProblemFromError(error, summary);
    if (problem) {
      notificationsStore.addProblem(problem);
      return problem.detail ?? problem.title;
    }

    const detail = getErrorDetail(error, fallbackDetail);
    notificationsStore.addError(summary, detail);
    return detail;
  }

  function sortNewestFirst (items: UploadFileItem[]): UploadFileItem[] {
    return [...items].sort((left, right) => toEpochMs(right.uploadedDate as string) - toEpochMs(left.uploadedDate as string));
  }

  function rankActiveBeforeArchived (items: UploadFileItem[]): UploadFileItem[] {
    const active = sortNewestFirst(items.filter((file) => !file.isArchived));
    const archived = sortNewestFirst(items.filter((file) => Boolean(file.isArchived)));
    return [...active, ...archived];
  }

  function applyStatusFilter (parents: UploadFileItem[]): UploadFileItem[] {
    if (selectedStatuses.value.length === 0) {
      return parents;
    }

    return parents.filter((parent) => {
      if (selectedStatuses.value.includes(parent.processStatus || '')) return true;

      const children = filteredFiles.value.filter((f) => f.extractedFromUploadId === parent.uploadId);
      return children.some((child) => selectedStatuses.value.includes(child.processStatus || ''));
    });
  }

  function clearUploadStatusFallbackTimer (uploadId?: string | null): void {
    if (!uploadId) return;

    const timerId = uploadStatusFallbackTimers.get(uploadId);
    if (!timerId) return;

    globalThis.clearTimeout(timerId);
    uploadStatusFallbackTimers.delete(uploadId);
  }

  function resolveTerminalState (file: UploadFileItem): UploadTerminalState {
    const status = normalizeStatus(file.processStatus);
    const message = String(file.currentMessage || file.message || '').toLowerCase();
    const hasPoisonHint = message.includes('poison');

    if (hasPoisonHint) return 'poisoned';
    if (status === 'timeout') return 'timedout';
    if (status === 'completed' || status === 'ready') return 'completed';
    if (status === 'failed' || status === 'cancelled' || status === 'aborted' || status === 'unknown' || file.hasError === true) {
      return 'failed';
    }
    if (status === 'uploading') return 'uploading';
    if (status === 'pending' || status === 'awaitinguserdecision') return 'processing';
    return 'processing';
  }

  function withTerminalState (file: UploadFileItem): UploadFileItem {
    return {
      ...file,
      terminalState: resolveTerminalState(file)
    };
  }

  function isTerminalState (file: UploadFileItem | null | undefined): boolean {
    if (!file) return false;
    return file.terminalState === 'completed'
      || file.terminalState === 'failed'
      || file.terminalState === 'timedout'
      || file.terminalState === 'poisoned';
  }

  function isTerminalFailure (file: UploadFileItem | null | undefined): boolean {
    if (!file) return false;
    return file.terminalState === 'failed'
      || file.terminalState === 'timedout'
      || file.terminalState === 'poisoned';
  }

  function isReadyForPreview (file: UploadFileItem | null | undefined): boolean {
    if (!file) return false;
    if (isTerminalFailure(file)) return false;
    if (file.isProcessing === true) return false;

    const status = normalizeStatus(file.processStatus);
    if (status.includes('ready') || status.includes('completed')) {
      return true;
    }

    return Boolean(file.processedDate || file.canImportFile || file.isReadyForImport);
  }

  function getUploadFailureMessage (file: UploadFileItem | null | undefined): string {
    const message = file?.currentMessage || file?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }

    return 'Filbehandling feilet før filen ble klar til forhåndsvisning.';
  }

  function beginUploadUiState (): void {
    uploadProgressPercent.value = 0;
    uploadShowProgressBar.value = true;
    uploadOverlayVisible.value = true;
    uploadOverlayMessage.value = 'Laster opp fil...';
    uploadOutcomeState.value = 'idle';
    uploadOutcomeMessage.value = null;
  }

  function setUploadProgress (progressPercent: number, statusText?: string): void {
    uploadProgressPercent.value = progressPercent;
    // statusText (f.eks. «Laster opp fil 2 av 5») settes ved flerfils-opplasting så overlayet
    // forklarer hva som skjer; ellers standard enkelt-fil-tekst.
    uploadOverlayMessage.value = statusText
      ? `${statusText} – ${progressPercent}%`
      : `Laster opp fil... ${progressPercent}%`;
  }

  function setUploadOverlayProcessingMessage (): void {
    uploadOverlayMessage.value = 'Behandler fil...';
  }

  function setUploadOverlayMessage (message: string): void {
    uploadOverlayMessage.value = message;
  }

  function finishUploadUiState (state: 'success' | 'error', message: string): void {
    uploadShowProgressBar.value = false;
    uploadOverlayVisible.value = false;
    uploadOutcomeState.value = state;
    uploadOutcomeMessage.value = message;
  }

  function clearUploadUiWorkingState (): void {
    uploadShowProgressBar.value = false;
    uploadOverlayVisible.value = false;
    uploadOverlayMessage.value = '';
  }

  function scheduleUploadStatusFallback (uploadId?: string | null): void {
    if (!uploadId) return;

    clearUploadStatusFallbackTimer(uploadId);
    const timerId = globalThis.setTimeout(async () => {
      uploadStatusFallbackTimers.delete(uploadId);
      await fetchFileStatus(uploadId);
    }, uploadStatusFallbackDelayMs);

    uploadStatusFallbackTimers.set(uploadId, timerId);
  }

  function handleEventStreamUpdate (eventStreamUpdate: EventStreamUploadUpdate): void {
    if (!eventStreamUpdate?.fileId) return;

    const fileIndex = files.value.findIndex((f) => f.uploadId === eventStreamUpdate.fileId);
    if (fileIndex === -1) {
      globalStore.debug(`${debugPrefix} EventStreamUpdate for unknown file: ${eventStreamUpdate.fileId}`);
      void fetchFileStatus(eventStreamUpdate.fileId);
      return;
    }

    clearUploadStatusFallbackTimer(eventStreamUpdate.fileId);

    const currentFile = files.value[fileIndex];
    files.value[fileIndex] = withTerminalState({
      ...currentFile,
      processType: eventStreamUpdate.processType || currentFile.processType,
      processStatus: eventStreamUpdate.processStatus || currentFile.processStatus,
      isComplete: eventStreamUpdate.isComplete ?? currentFile.isComplete,
      hasError: eventStreamUpdate.hasError ?? currentFile.hasError,
      progressPercent: eventStreamUpdate.progressPercent ?? currentFile.progressPercent,
      message: eventStreamUpdate.message || currentFile.message,
      currentMessage: eventStreamUpdate.message || currentFile.currentMessage,
      currentTaskName: eventStreamUpdate.taskName || currentFile.currentTaskName,
      timestamp: eventStreamUpdate.timestamp || currentFile.timestamp,
      counterData: eventStreamUpdate.counterData || currentFile.counterData,
    });

    globalStore.debug(`${debugPrefix} Updated file ${eventStreamUpdate.fileId} from SignalR EventStreamUpdate`);

    if (eventStreamUpdate.isComplete === true) {
      void fetchFileStatus(eventStreamUpdate.fileId);
    }
  }

  function toggleArchive (): void {
    showArchive.value = !showArchive.value;
    clearStatusFilter();
    globalStore.debug(`${debugPrefix} toggleArchive`, showArchive.value);
  }

  const filteredFiles = computed<UploadFileItem[]>(() => files.value);

  const filteredFilesTopLevel = computed<UploadFileItem[]>(() =>
    filteredFiles.value.filter((f) => !f.extractedFromUploadId),
  );

  const allFiles = computed<UploadFileItem[]>(() => {
    const topLevelFiles = filteredFilesTopLevel.value;

    if (listContext.value === 'picker') {
      if (query.value.trim() || pickerSearchActivated.value) {
        return rankActiveBeforeArchived(topLevelFiles);
      }

      if (uploadedInSessionIds.value.length > 0) {
        const uploadedIds = new Set(uploadedInSessionIds.value);
        return rankActiveBeforeArchived(topLevelFiles.filter((file) => uploadedIds.has(file.uploadId)));
      }

      return [];
    }

    if (showArchive.value) {
      return sortNewestFirst(topLevelFiles.filter((file) => Boolean(file.isArchived)));
    }

    return rankActiveBeforeArchived(topLevelFiles);
  });

  const visibleFiles = computed<UploadFileItem[]>(() => {
    return applyStatusFilter(allFiles.value);
  });

  const loadedTopLevelCount = computed<number>(() =>
    allFiles.value.filter((file) => !file.extractedFromUploadId).length
  );
  const hasMoreItems = computed<boolean>(() =>
    totalFilesCount.value > 0 && loadedTopLevelCount.value < totalFilesCount.value
  );
  const itemsShowing = computed<number>(() => visibleFiles.value.length);

  function toggleStatus (status: string): void {
    const idx = selectedStatuses.value.indexOf(status);
    if (idx === -1) {
      selectedStatuses.value.push(status);
      return;
    }
    selectedStatuses.value.splice(idx, 1);
  }

  function clearStatusFilter (): void {
    selectedStatuses.value = [];
  }

  async function loadMore (): Promise<void> {
    if (!hasMoreItems.value) {
      return;
    }

    const previousPage = currentPage.value;
    currentPage.value = previousPage + 1;
    try {
      await getFiles({ append: true });
    } catch (error) {
      currentPage.value = previousPage;
      throw error;
    }
  }

  function resetPagination (): void {
    currentPage.value = 1;
  }

  function notifySelectedFileChange (): void {
    const next = normalizedUploadIdOrNull(selectedFile.value?.uploadId);
    if (previousNotifiedSelectedUploadId === next) {
      return;
    }
    // Tilsvarer watch uten { immediate: true }: ikke kall lyttere første gang tilstanden er «ingen valgt fil».
    if (previousNotifiedSelectedUploadId === undefined && next === null) {
      previousNotifiedSelectedUploadId = null;
      return;
    }
    previousNotifiedSelectedUploadId = next;
    for (const handler of selectedFileChangeHandlers.values()) {
      handler(next);
    }
  }

  function selectFile (file: UploadFileItem): void {
    const incomingId = normalizedUploadIdOrNull(file?.uploadId) ?? '';
    const currentId = normalizedUploadIdOrNull(selectedFile.value?.uploadId) ?? '';
    if (incomingId.length > 0 && incomingId === currentId) {
      selectedFile.value = null;
      globalStore.debug(`${debugPrefix} selectFile`, file, 'deselected');
      notifySelectedFileChange();
      return;
    }

    selectedFile.value = file;
    globalStore.debug(`${debugPrefix} selectFile`, file, 'selected');
    notifySelectedFileChange();
  }

  // Bug #2259: idempotent «sett valgt fil» for PROGRAMMATISKE valg-stier (preview-
  // resolving, arkiv-/zip-valg, init). Til forskjell fra `selectFile` toggler den
  // ALDRI — samme id holder valget. `selectFile` (toggle) er reservert brukerens
  // klikk-for-å-avvelge i fil-lista. Programmatiske kall som re-entrer via
  // `onSelectedFileChange` (f.eks. ShellStepFileSelection.onSelectedChanged) ville
  // ellers treffe toggle-grenen og nulle ut selectedFile (fil-plukkeren ble stående
  // uten forhåndsvisning). `notifySelectedFileChange` deduper på uendret id, så et
  // re-entrant kall med samme id varsler ikke på nytt → ingen loop.
  function setSelectedFile (file: UploadFileItem): void {
    selectedFile.value = file;
    globalStore.debug(`${debugPrefix} setSelectedFile`, file, 'selected');
    notifySelectedFileChange();
  }

  function isImportableFile (file: UploadFileItem | null | undefined): boolean {
    if (!file) return false;
    if (file.isInBlobStorage === false) return false;
    if (file.isExcel === true && file.isExcelSheet !== true) return false;
    return true;
  }

  function getIconFromContentType (file: UploadFileItem): string[] {
    if (file.isProcessing) {
      return ['fal', 'spinner'];
    }

    if (file.isExcelSheet) {
      return ['fal', 'table'];
    }

    if (dictionaryHasKey(icons, file.contentType)) {
      return icons[file.contentType as string];
    }

    return ['fal', 'file-circle-exclamation'];
  }

  function getColorFromContentType (file: UploadFileItem): string {
    if (dictionaryHasKey(colors, file.contentType)) {
      return colors[file.contentType as string];
    }

    return 'red';
  }

  function clearQuery (): void {
    query.value = '';
  }

  // WI 2286: expand/collapse av et arkiv (Excel/zip) per rad. Default collapsed.
  function isExpanded (uploadId: string): boolean {
    return expandedUploadIds.has(uploadId);
  }

  function toggleExpanded (uploadId: string): void {
    if (expandedUploadIds.has(uploadId)) {
      expandedUploadIds.delete(uploadId);
    } else {
      expandedUploadIds.add(uploadId);
    }
  }

  async function updateImportDefinitionKey (uploadId: string, importDefinitionKey: string): Promise<void> {
    globalStore.debug(`${debugPrefix} updateImportDefinitionKey`, uploadId, importDefinitionKey);
    try {
      await axiosClient.put(`${uploadApiBasePath}/import-definition-key`, null, {
        params: { uploadId, importDefinitionKey },
      });

      resetPagination();
      await getFiles();
    } catch (error: unknown) {
      notifyApiError('Kunne ikke oppdatere importmal', error);
      throw error;
    }
  }

  async function updateMetadata (uploadId: string, metadata: unknown): Promise<void> {
    if (!uploadId) {
      notificationsStore.addError('Kan ikke lagre til ukjent fil-entitet');
      return;
    }

    if (!metadata) {
      notificationsStore.addError('Kan ikke lagre tomt skjema');
      return;
    }

    globalStore.debug(`${debugPrefix} update`, uploadId, metadata);
    try {
      await axiosClient.put(`${uploadApiBasePath}/file-metadata`, metadata, { params: { uploadId } });
    } catch (error: unknown) {
      notifyApiError('Kunne ikke lagre metadata', error);
      throw error;
    }
  }

  function updateOrAddFile (newFile: UploadFileItem): void {
    if (!newFile?.uploadId) {
      console.warn(`${debugPrefix} updateOrAddFile(): Missing uploadId`, newFile);
      return;
    }

    clearUploadStatusFallbackTimer(newFile.uploadId);
    const index = files.value.findIndex((f) => f.uploadId === newFile.uploadId);

    if (index !== -1) {
      files.value[index] = withTerminalState({ ...files.value[index], ...newFile });
      globalStore.debug(`${debugPrefix} updateOrAddFile(): Updated file`, newFile);
      return;
    }

    files.value.unshift(withTerminalState(newFile));
    globalStore.debug(`${debugPrefix} updateOrAddFile(): Added new file`, newFile);
  }

  async function getFiles (options: GetFilesOptions = {}): Promise<void> {
    const { resetArchive = false, append = false } = options;
    loading.value = true;
    try {
      if (resetArchive) {
        showArchive.value = false;
        selectedStatuses.value = [];
        query.value = '';
        resetPagination();
      }

      const response = await getUploadFilesApi<UploadFileItem>(
        query.value,
        currentPage.value,
        pageSize.value
      );
      if (response) {
        const nextItems = (response.items || []).map(withTerminalState);
        if (append) {
          const mergedById = new Map<string, UploadFileItem>();
          for (const file of files.value) {
            mergedById.set(file.uploadId, file);
          }
          for (const file of nextItems) {
            mergedById.set(file.uploadId, file);
          }
          files.value = Array.from(mergedById.values());
        } else {
          files.value = nextItems;
        }
        totalFilesCount.value = response.totalCount ?? files.value.length;
        globalStore.debug(`${debugPrefix} getFiles()`, { itemsCount: files.value.length });
      }
    } catch (error: unknown) {
      notifyApiError('Kunne ikke hente filer', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  function clearSelection (): void {
    // WI 2317: containeren (selectedFile) er den ENE FE-holdte fil-referansen.
    // Den gamle FE-synkede datasett-/ark-valg-staten (selectedDataset +
    // userSheetChoiceUploadId) er fjernet — det resolverte datasettet er BE-eid
    // på wizard-draften, og FE leser det derfra.
    selectedFile.value = null;
    notifySelectedFileChange();
  }

  function requestPreview (payload: { rootUploadId?: string; previewUploadId?: string }): void {
    previewRequestCounter.value += 1;
    const request: UploadPreviewRequest = {
      requestId: previewRequestCounter.value,
      rootUploadId: payload?.rootUploadId,
      previewUploadId: payload?.previewUploadId
    };
    previewRequest.value = request;

    for (const handler of previewRequestHandlers.values()) {
      void Promise.resolve(handler(request)).catch((error: unknown) => {
        notifyApiError('Feil ved åpning av forhåndsvisning', error);
      });
    }
  }

  function clearPreviewRequest (requestId?: number): void {
    if (requestId && previewRequest.value?.requestId !== requestId) {
      return;
    }
    previewRequest.value = null;
  }

  function onPreviewRequest (handler: UploadPreviewRequestHandler): () => void {
    previewRequestHandlerCounter.value += 1;
    const handlerId = previewRequestHandlerCounter.value;
    previewRequestHandlers.set(handlerId, handler);

    return () => {
      previewRequestHandlers.delete(handlerId);
    };
  }

  function onSelectedFileChange (handler: UploadSelectedFileChangeHandler): () => void {
    selectedFileChangeHandlerNextId += 1;
    const handlerId = selectedFileChangeHandlerNextId;
    selectedFileChangeHandlers.set(handlerId, handler);

    return () => {
      selectedFileChangeHandlers.delete(handlerId);
    };
  }

  async function initializeFileList (
    context: UploadListContext = 'picker',
    options: InitializeFileListOptions = {}
  ): Promise<void> {
    const { prefetchOnMount = false, initialQuery = '' } = options;
    listContext.value = context;
    currentPage.value = 1;
    showArchive.value = false;
    query.value = initialQuery;
    clearStatusFilter();
    clearSelection();
    uploadedInSessionIds.value = [];
    pickerSearchActivated.value = false;

    if (prefetchOnMount || initialQuery.trim().length > 0) {
      await getFiles({ resetArchive: false });
    }
  }

  function resetListUiState (): void {
    listContext.value = 'picker';
    showArchive.value = false;
    query.value = '';
    selectedStatuses.value = [];
    uploadedInSessionIds.value = [];
    pickerSearchActivated.value = false;
    clearSelection();
  }

  async function searchFiles (searchValue = ''): Promise<void> {
    pickerSearchActivated.value = true;
    query.value = searchValue;
    resetPagination();
    await getFiles();
  }

  function close (): void {
    resetListUiState();
    globalStore.debug(`${debugPrefix} close()`, 'Dialog UI state reset');
  }

  function reset (): void {
    resetListUiState();
    globalStore.debug(`${debugPrefix} reset()`, 'All state reset');
  }

  async function downloadFile ({ uploadId, fileName }: { uploadId: string; fileName: string }): Promise<void> {
    try {
      const response = await axiosClient.get(`${uploadApiBasePath}/file`, {
        responseType: 'blob',
        params: { uploadId },
      });

      if (!response?.data) return;

      downloadBlob(new Blob([response.data]), fileName);
      notificationsStore.addSuccess(`Lastet ned ${fileName}`);
    } catch (error: unknown) {
      notifyApiError('Kunne ikke laste ned fil', error);
      throw error;
    }
  }

  async function deleteFile ({ uploadId, fileName }: { uploadId: string; fileName: string }): Promise<void> {
    try {
      const response = await axiosClient.delete(`${uploadApiBasePath}/file`, { params: { uploadId } });
      resetPagination();
      await getFiles();
      if (response) {
        notificationsStore.addSuccess(`Slettet ${fileName}`);
      }
    } catch (error: unknown) {
      notifyApiError('Kunne ikke slette fil', error);
      throw error;
    }
  }

  async function archiveFile ({ uploadId, fileName }: { uploadId: string; fileName: string }): Promise<void> {
    try {
      const response = await axiosClient.put(`${uploadApiBasePath}/file/${uploadId}/archive`);
      resetPagination();
      await getFiles();
      if (response) {
        notificationsStore.addSuccess(`Arkiverte ${fileName}`);
      }
    } catch (error: unknown) {
      notifyApiError('Kunne ikke arkivere fil', error);
      throw error;
    }
  }

  async function retryFile (uploadId: string): Promise<void> {
    globalStore.debug(`${debugPrefix} retryFile`, uploadId);
    try {
      const response = await axiosClient.put(`${uploadApiBasePath}/file/${uploadId}/retry`);
      resetPagination();
      await getFiles();
      if (response) {
        notificationsStore.addSuccess('Filen vil bli behandlet på nytt');
      }
    } catch (error: unknown) {
      notifyApiError('Kunne ikke starte ny behandling av fil', error);
      throw error;
    }
  }

  async function recalculateFileMetrics (uploadId: string): Promise<void> {
    recalculateStatus.value = 'loading';
    recalculateError.value = null;

    if (!uploadId) {
      const detail = 'Mangler uploadId';
      recalculateStatus.value = 'error';
      recalculateError.value = detail;
      notificationsStore.addError('Kan ikke rekalkulere ark', detail);
      return;
    }

    try {
      await axiosClient.put(`${uploadApiBasePath}/file/${uploadId}/recalculate`);
      resetPagination();
      await getFiles();
      recalculateStatus.value = 'success';
      notificationsStore.addSuccess('Ark-metrikker er rekalkulert');
    } catch (error: unknown) {
      const detail = notifyApiError('Kunne ikke rekalkulere ark', error);
      recalculateStatus.value = 'error';
      recalculateError.value = detail;
    }
  }

  async function postFiles (
    selectedFiles: Iterable<File>,
    progressUpdate: (progressPercent: number) => void,
    excelConverter: string | null = null,
  ): Promise<unknown> {
    const formData = new FormData();
    for (const file of selectedFiles) {
      formData.append('files', file, file.name);
    }
    // WI 2430: valgfri per-opplasting import-lesemetode-override (kun ikke-prod; serveren gater i prod).
    if (excelConverter) {
      formData.append('excelConverter', excelConverter);
    }

    try {
      const response = await axiosClient.post<string[]>(uploadApiBasePath, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress (progressEvent) {
          if (!progressEvent.total) return;
          progressUpdate(Number.parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString(), 10));
        },
      });

      const uploadIds = Array.isArray(response?.data) ? response.data : [];
      uploadedInSessionIds.value = Array.from(new Set([...uploadedInSessionIds.value, ...uploadIds]));
      for (const uploadId of uploadIds) {
        scheduleUploadStatusFallback(uploadId);
      }
      resetPagination();
      await getFiles();

      return response?.data;
    } catch (error: unknown) {
      notifyApiError('Kunne ikke laste opp filer', error);
      throw error;
    }
  }

  // WI 2303: direkte-til-blob-opplasting. Henter en kortlevd write-only SAS (2300),
  // PUT-er fila i blokker rett mot blob-lageret via @azure/storage-blob, og registrerer
  // bloben (2301) — uten å sende bytene gjennom API-laget. SDK-en lazy-importeres KUN her
  // så den havner i en egen code-split-chunk (`vendor-azure-storage`) og ikke i hovedbundle.
  // SAS + register går via axiosClient (auto-injisert Bearer + Ics-Project-Id); blob-PUT-en
  // går mot SAS-URL-en på en annen origin uten auth-header, derfor rå SDK (ikke axiosClient).
  async function directUpload (
    input: File | Iterable<File>,
    progressUpdate: (progressPercent: number, statusText?: string) => void,
    excelConverter: string | null = null,
  ): Promise<string | null> {
    // fileUpload() sender en FileList (openFileDialog: input.multiple) eller en DropZone-
    // iterable — ikke nødvendigvis én enkelt File. Normaliser til en File-liste slik postFiles
    // gjør, så .name/.type leses på den ENKELTE fila og ikke på samlingen (ellers blir SAS-body
    // {} → 400). Roll-forward: feiler en fil, kastes den; allerede opplastede filer blir stående.
    const files = input instanceof File ? [input] : Array.from(input);
    const { BlockBlobClient } = await import('@azure/storage-blob');
    const uploadIds: string[] = [];

    // Aggregert fremdrift over ALLE filers bytes (ikke per-fil-reset som «hopper»), + «fil X av N»-
    // tekst når det er flere filer, så overlayet forklarer hva som skjer i stedet for bare en spinner.
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    let completedBytes = 0;

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const statusText = files.length > 1 ? `Laster opp fil ${index + 1} av ${files.length}` : undefined;
      let sas: IssueUploadSasResponse;
      try {
        const sasResponse = await axiosClient.post<IssueUploadSasResponse>(`${uploadApiBasePath}/sas`, {
          fileName: file.name,
          contentType: file.type,
        });
        sas = sasResponse.data;
      } catch (error: unknown) {
        notifyApiError('Kunne ikke starte opplasting', error);
        throw error;
      }

      try {
        await new BlockBlobClient(sas.uploadUri).uploadData(file, {
          blockSize: directUploadBlockSizeBytes,
          concurrency: directUploadConcurrency,
          onProgress: (progressEvent) => {
            if (totalBytes <= 0) return;
            const overallPercent = Math.round(((completedBytes + progressEvent.loadedBytes) / totalBytes) * 100);
            if (statusText) {
              progressUpdate(overallPercent, statusText);
            } else {
              progressUpdate(overallPercent);
            }
          },
        });
      } catch (error: unknown) {
        notifyApiError('Opplasting til fillager feilet', error);
        throw error;
      }

      try {
        await axiosClient.post<RegisterUploadedBlobResponse>(`${uploadApiBasePath}/register`, {
          uploadId: sas.uploadId,
          fileName: file.name,
          // WI 2430: valgfri per-opplasting import-lesemetode-override (kun ikke-prod; serveren gater i prod).
          ...(excelConverter ? { excelConverter } : {}),
        });
      } catch (error: unknown) {
        notifyApiError('Kunne ikke registrere opplastet fil', error);
        throw error;
      }

      completedBytes += file.size;
      uploadIds.push(sas.uploadId);
    }

    uploadedInSessionIds.value = Array.from(new Set([...uploadedInSessionIds.value, ...uploadIds]));
    for (const uploadId of uploadIds) {
      scheduleUploadStatusFallback(uploadId);
    }
    resetPagination();
    await getFiles();

    return uploadIds[0] ?? null;
  }

  function getFileByUploadId (uploadId: string): UploadFileItem | null {
    return files.value.find((f) => f.uploadId === uploadId) || null;
  }

  function getFilesByExtractedFromUploadId (parentUploadId: string): UploadFileItem[] {
    return files.value.filter((f) => f.extractedFromUploadId === parentUploadId);
  }

  async function fetchFileStatus (uploadId: string): Promise<void> {
    if (!uploadId) return;

    try {
      const response = await axiosClient.get<UploadFileItem>(`${uploadApiBasePath}/file/${uploadId}/status`);
      if (response?.data) {
        updateOrAddFile(response.data);
      }
    } catch (error: unknown) {
      notifyApiError('Kunne ikke hente filstatus', error);
    }
  }

  async function findImportDefinition (uploadId: string): Promise<void> {
    findImportDefinitionStatus.value = 'loading';
    findImportDefinitionError.value = null;

    if (!uploadId) {
      const detail = 'Mangler uploadId';
      findImportDefinitionStatus.value = 'error';
      findImportDefinitionError.value = detail;
      notificationsStore.addError('Kan ikke finne importmal', detail);
      return;
    }

    try {
      await axiosClient.post(`${uploadApiBasePath}/find-import-definition`, null, {
        params: { uploadId },
      });
      resetPagination();
      await getFiles();
      findImportDefinitionStatus.value = 'success';
      notificationsStore.addSuccess('Importmal-søk er fullført');
    } catch (error: unknown) {
      const detail = notifyApiError('Kunne ikke finne importmal', error);
      findImportDefinitionStatus.value = 'error';
      findImportDefinitionError.value = detail;
    }
  }

  async function waitForUploadOutcome (uploadId: string, options: WaitForUploadOutcomeOptions = {}): Promise<WaitForUploadOutcomeResult> {
    // 5 min fallback-timeout: Excel-pipelinen (blob-download + EPPlus-parse + child-
    // blob-upload + re-enqueue) kan ta opptil ~2 min for moderate filer. SignalR-events
    // er primær signaltransport; pollingen her er kun disconnect-fallback.
    const timeoutMs = options.timeoutMs ?? 300000;
    const pollIntervalMs = options.pollIntervalMs ?? 1200;
    const notifyOnFailure = options.notifyOnFailure ?? true;
    const startedAt = Date.now();

    while ((Date.now() - startedAt) < timeoutMs) {
      await fetchFileStatus(uploadId);
      const candidate = getFileByUploadId(uploadId);
      const progressMessage = candidate?.currentMessage || candidate?.message;
      if (typeof progressMessage === 'string' && progressMessage.length > 0) {
        options.onProgressMessage?.(progressMessage);
      }

      if (isReadyForPreview(candidate)) {
        return {
          file: candidate,
          isReadyForPreview: true,
          isTerminalFailure: false,
          didTimeout: false,
          resultMessage: null
        };
      }

      if (isTerminalFailure(candidate)) {
        const failureMessage = getUploadFailureMessage(candidate);
        if (notifyOnFailure) {
          notificationsStore.addError('Filbehandling feilet', failureMessage);
        }
        return {
          file: candidate,
          isReadyForPreview: false,
          isTerminalFailure: true,
          didTimeout: false,
          resultMessage: failureMessage
        };
      }

      if (isTerminalState(candidate)) {
        return {
          file: candidate,
          isReadyForPreview: false,
          isTerminalFailure: false,
          didTimeout: false,
          resultMessage: null
        };
      }

      await new Promise((resolve) => globalThis.setTimeout(resolve, pollIntervalMs));
    }

    if (notifyOnFailure) {
      notificationsStore.addError(
        'Filbehandling tok uventet lang tid',
        'Excel-ekstraksjon eller analyse er ikke ferdig etter 5 minutter. Sjekk Azure Functions-status eller prøv på nytt.');
    }

    return {
      file: getFileByUploadId(uploadId),
      isReadyForPreview: false,
      isTerminalFailure: false,
      didTimeout: true,
      resultMessage: 'Excel-ekstraksjon eller analyse er ikke ferdig etter 5 minutter.'
    };
  }

  return {
    loading,
    files,
    postFiles,
    directUpload,
    getFiles,
    initializeFileList,
    handleEventStreamUpdate,
    downloadFile,
    deleteFile,
    archiveFile,
    retryFile,
    getIconFromContentType,
    getColorFromContentType,
    humanReadableFileSize,
    updateImportDefinitionKey,
    updateMetadata,
    toggleArchive,
    query,
    filteredFiles,
    filteredFilesTopLevel,
    clearQuery,
    isExpanded,
    toggleExpanded,
    searchFiles,
    updateOrAddFile,
    selectedFile,
    selectFile,
    setSelectedFile,
    clearSelection,
    showArchive,
    listContext,
    hasMoreItems,
    itemsShowing,
    loadMore,
    resetPagination,
    allFiles,
    visibleFiles,
    totalFilesCount,
    findImportDefinitionStatus,
    findImportDefinitionError,
    recalculateStatus,
    recalculateError,
    close,
    reset,
    selectedStatuses,
    toggleStatus,
    clearStatusFilter,
    requestPreview,
    clearPreviewRequest,
    onPreviewRequest,
    onSelectedFileChange,
    getFileByUploadId,
    getFilesByExtractedFromUploadId,
    fetchFileStatus,
    findImportDefinition,
    recalculateFileMetrics,
    isImportableFile,
    isTerminalState,
    isTerminalFailure,
    waitForUploadOutcome,
    uploadProgressPercent,
    uploadShowProgressBar,
    uploadOverlayVisible,
    uploadOverlayMessage,
    uploadOutcomeState,
    uploadOutcomeMessage,
    beginUploadUiState,
    setUploadProgress,
    setUploadOverlayProcessingMessage,
    setUploadOverlayMessage,
    finishUploadUiState,
    clearUploadUiWorkingState,
  };
});
